import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
interface UserManagementStats {
  totalUsers: number;
}

// -----------------------------------------------------------------------------
interface UseUserManagementStatsReturn {
  stats: UserManagementStats;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// -----------------------------------------------------------------------------
export const useUserManagementStats = (): UseUserManagementStatsReturn => {
  const medplum = useMedplum();
  const { t } = useTranslation(['admin', 'common']);
  const [stats, setStats] = useState<UserManagementStats>({
    totalUsers: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const totalPractitioners = await medplum.searchResources('Practitioner', {
        _total: 'estimate',
        _count: '0',
      });

      const total = totalPractitioners?.bundle?.total ?? 0;

      setStats({
        totalUsers: total,
      });
    } catch (err) {
      console.error(t('errors.loadingStats', { ns: 'admin' }), err);
      setError(t('errors.loadingStats', { ns: 'admin' }));
    } finally {
      setLoading(false);
    }
  }, [medplum, t]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ---------------------------------------------------------------------------
  return {
    stats,
    loading,
    error,
    refetch: loadStats,
  };
};
